/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Home Deferred Runtime
   Project: La Troupe
   Version: 1.9.2
   Scope: Home performance runtime
   ----------------------------------------------------------
   1. Editorial mega-menu CSS + product hydration load only
      when the user actually approaches/focuses the header.
   2. LLCL mega product imagery is rewritten to responsive
      Shopify CDN variants before original-size downloads.
   3. Below-fold editorial video sources are dehydrated on
      initial Home load and restored only when playback starts.
   4. Heavy below-fold LLCL interaction engines hydrate only
      when their sections are close to the viewport.
   5. Heavy mobile drawer subpanel payloads are parked outside
      the live DOM until menu intent, while Prestige shells,
      IDs, aria-controls and visible styling remain untouched.
   6. Desktop mega-menu block trees are detached on initial
      mobile Home loads because that layer is CSS-hidden, then
      restored in-place before the viewport enters desktop.
   7. Home-only accessibility semantics normalize ARIA-labeled
      generic groups, early control/link names, decorative image
      alternatives and hidden-scene focus state without visual changes.
   ========================================================== */
(() => {
  'use strict';

  const boot = document.querySelector('script[data-ll-home-runtime]');
  if (!boot) return;

  const desktop = window.matchMedia('(min-width: 700px)');
  const HEADER = '.ll-header--llcl';
  const MEGA_IMAGE_CLASS = 'll-mega-editorial-product__image';
  const MEGA_IMAGE_WIDTHS = [240, 320, 480, 640, 800];
  const MEGA_IMAGE_SIZES = '(min-width: 1440px) 360px, (min-width: 1000px) 300px, 240px';
  const VIDEO_SELECTOR = 'video[data-ll-video]';
  const DEFERRED_SECTION_SCRIPT = 'script[data-ll-home-deferred-section-src]';
  const DEFERRED_SECTION_ROOT_MARGIN = '400px 0px';

  const MOBILE_SIDEBAR = '#sidebar-menu';
  const MOBILE_PANEL = '.header-sidebar__sub-panel';
  const MOBILE_PANEL_CONTENT = '.ll-mobile-drawer__panel-content';
  const MOBILE_PANEL_FOOTER = '.ll-mobile-drawer__sub-footer';
  const MOBILE_PANEL_TRIGGER = '.header-sidebar__linklist-button[aria-controls]';
  const MOBILE_DRAWER_TRIGGER = '[aria-controls="sidebar-menu"]';
  const MOBILE_HIDDEN_MEGA_LAYER = '.ll-header__mega-layer';
  const MOBILE_HIDDEN_MEGA_BLOCK = '.ll-header__mega-layer [data-ll-mega-block]';

  const sectionScriptLoads = new Set();
  const mobilePanelPayloads = new WeakMap();
  const mobileMegaBlockPayloads = [];
  let started = false;
  let mobileSidebarGuardInstalled = false;
  let mobileMegaLayerGuardInstalled = false;

  const cssUrls = [boot.dataset.megaCss, boot.dataset.megaC06bCss].filter(Boolean);
  const jsUrls = [boot.dataset.megaJs, boot.dataset.iconsJs].filter(Boolean);

  const installHomeA11ySemantics = () => {
    document
      .querySelectorAll(
        '.ll-silhouette__index[role="list"], .ll-silhouette__row[role="listitem"]'
      )
      .forEach((node) => node.removeAttribute('role'));

    document
      .querySelectorAll('.ll-places__track[role="list"]')
      .forEach((track) => {
        track.removeAttribute('role');
        track.removeAttribute('aria-label');
      });

    document
      .querySelectorAll('.ll-places__card[role="listitem"]')
      .forEach((card) => card.removeAttribute('role'));

    document
      .querySelectorAll('.ll-trust-v5__ledger[role="list"]')
      .forEach((node) => node.removeAttribute('role'));

    document
      .querySelectorAll('.ll-trust-v5__item[role="listitem"]')
      .forEach((node) => node.removeAttribute('role'));

    document
      .querySelectorAll(
        '[data-ll-icons-track][aria-label], .ll-icons-journey__counter[aria-label], .ll-trust-v5__marquee[aria-label]'
      )
      .forEach((node) => {
        if (!node.hasAttribute('role')) node.setAttribute('role', 'group');
      });

    document
      .querySelectorAll(
        '.ll-icons-journey__media-link[aria-label] .ll-icons-journey__image--primary'
      )
      .forEach((image) => {
        const link = image.closest('.ll-icons-journey__media-link[aria-label]');
        const alt = image.getAttribute('alt')?.trim();
        const label = link?.getAttribute('aria-label')?.trim();

        if (alt && label && alt === label) {
          image.setAttribute('alt', '');
        }
      });

    document
      .querySelectorAll('.ll-trust-v5__toggle[data-ll-trust-toggle]')
      .forEach((button) => {
        if (button.getAttribute('aria-label')?.trim()) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        const selector = expanded
          ? '.ll-trust-v5__toggle-open'
          : '.ll-trust-v5__toggle-closed';
        const label = button.querySelector(selector)?.textContent?.trim();

        if (label) button.setAttribute('aria-label', label);
      });

    document
      .querySelectorAll('.ll-announcement-mx__cta:not([aria-label])')
      .forEach((link) => {
        const label = link
          .querySelector('span:not([aria-hidden="true"])')
          ?.textContent?.trim();

        if (label) link.setAttribute('aria-label', label);
      });

    document
      .querySelectorAll(
        '.ll-editorial-scroll-hero__media img:not([alt]), video.ll-savoir-film__video img:not([alt])'
      )
      .forEach((image) => image.setAttribute('alt', ''));

    document
      .querySelectorAll(
        '[data-ll-desktop-scene][aria-hidden="true"], [data-ll-mobile-scene][aria-hidden="true"]'
      )
      .forEach((scene) => {
        scene.setAttribute('inert', '');
      });
  };

  const withImageWidth = (raw, width) => {
    if (!raw || typeof raw !== 'string') return raw;

    try {
      const url = new URL(raw, window.location.href);
      const isShopifyImage =
        url.hostname === 'cdn.shopify.com' ||
        url.pathname.includes('/cdn/shop/') ||
        url.pathname.includes('/s/files/');

      if (!isShopifyImage) return raw;

      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return raw;
    }
  };

  const installMegaImageGuard = () => {
    if (window.__LLCL_MEGA_IMAGE_GUARD__) return;

    const proto = window.HTMLImageElement?.prototype;
    if (!proto) return;

    const descriptor = Object.getOwnPropertyDescriptor(proto, 'src');
    if (!descriptor?.get || !descriptor?.set || descriptor.configurable === false) return;

    window.__LLCL_MEGA_IMAGE_GUARD__ = true;

    Object.defineProperty(proto, 'src', {
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,
      get: descriptor.get,
      set(value) {
        if (this.classList?.contains(MEGA_IMAGE_CLASS) && typeof value === 'string') {
          const optimized = withImageWidth(value, 480);

          if (optimized !== value) {
            const srcset = MEGA_IMAGE_WIDTHS
              .map((width) => `${withImageWidth(value, width)} ${width}w`)
              .join(', ');

            this.setAttribute('srcset', srcset);
            this.setAttribute('sizes', MEGA_IMAGE_SIZES);
            this.setAttribute('fetchpriority', 'low');
            this.dataset.llclMegaOptimized = '1';
            descriptor.set.call(this, optimized);
            return;
          }
        }

        descriptor.set.call(this, value);
      }
    });
  };

  const installVideoPayloadGuard = () => {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (video.dataset.llclPayloadGuard === '1') return;

      const sources = Array.from(video.querySelectorAll('source[src]'));
      const directSrc = video.getAttribute('src');
      if (!sources.length && !directSrc) return;

      const nativePlay = video.play.bind(video);

      sources.forEach((source) => {
        const src = source.getAttribute('src');
        if (!src) return;
        source.dataset.llclSrc = src;
        source.removeAttribute('src');
      });

      if (directSrc) {
        video.dataset.llclSrc = directSrc;
        video.removeAttribute('src');
      }

      video.preload = 'none';
      video.dataset.llclPayloadGuard = '1';

      try {
        video.pause();
        video.load();
      } catch (_) {}

      const hydrateSources = () => {
        if (video.dataset.llclSourcesHydrated === '1') return;

        sources.forEach((source) => {
          const src = source.dataset.llclSrc;
          if (src) source.setAttribute('src', src);
        });

        const storedDirectSrc = video.dataset.llclSrc;
        if (storedDirectSrc) video.setAttribute('src', storedDirectSrc);

        video.dataset.llclSourcesHydrated = '1';
        video.load();
      };

      video.play = (...args) => {
        hydrateSources();
        return nativePlay(...args);
      };
    });
  };

  const loadCss = (href) => new Promise((resolve) => {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.llHomeDeferred = 'mega-css';
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', resolve, { once: true });
    document.head.appendChild(link);
  });

  const loadScript = (src) => new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.llHomeDeferred = 'mega-js';
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });

  const loadSectionScript = (marker) => {
    if (!marker || marker.dataset.llLoaded === '1') return;

    const src = marker.dataset.llHomeDeferredSectionSrc;
    if (!src) return;

    marker.dataset.llLoaded = '1';

    if (sectionScriptLoads.has(src) || document.querySelector(`script[src="${src}"]`)) {
      return;
    }

    sectionScriptLoads.add(src);

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.llHomeDeferred = 'section-js';
    script.addEventListener('error', () => {
      sectionScriptLoads.delete(src);
      marker.dataset.llLoaded = '0';
    }, { once: true });
    document.head.appendChild(script);
  };

  const observeNearViewport = (root, onEnter) => {
    if (!root || typeof onEnter !== 'function') return;

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      onEnter();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        onEnter();
      },
      { rootMargin: DEFERRED_SECTION_ROOT_MARGIN }
    );

    observer.observe(root);
  };

  const installDeferredSectionScripts = () => {
    document.querySelectorAll(DEFERRED_SECTION_SCRIPT).forEach((marker) => {
      const selector = marker.dataset.llHomeDeferredSectionRoot;
      const root = selector ? document.querySelector(selector) : null;

      if (!root) {
        loadSectionScript(marker);
        return;
      }

      observeNearViewport(root, () => loadSectionScript(marker));
    });
  };

  const installDeferredInlineInitializers = () => {
    const iconsRoot = document.querySelector('[data-ll-icons-journey]');
    const initIcons = window.__LLCL_DEFERRED_ICONS_INIT__;

    if (!iconsRoot || typeof initIcons !== 'function') return;

    observeNearViewport(iconsRoot, () => {
      requestAnimationFrame(initIcons);
    });
  };

  const parkChildren = (host) => {
    const fragment = document.createDocumentFragment();
    if (!host) return fragment;

    while (host.firstChild) {
      fragment.appendChild(host.firstChild);
    }

    return fragment;
  };

  const hydrateMobilePanel = (panel) => {
    const payload = panel ? mobilePanelPayloads.get(panel) : null;
    if (!payload || payload.hydrated) return;

    if (payload.contentHost && payload.contentFragment) {
      payload.contentHost.appendChild(payload.contentFragment);
    }

    if (payload.footerHost && payload.footerFragment) {
      payload.footerHost.appendChild(payload.footerFragment);
    }

    payload.hydrated = true;
    panel.dataset.llclPayloadParked = '0';
    panel.dataset.llclPayloadHydrated = '1';
  };

  const installMobileSidebarDomGuard = () => {
    if (mobileSidebarGuardInstalled || window.Shopify?.designMode) return;

    const sidebar = document.querySelector(MOBILE_SIDEBAR);
    if (!sidebar) return;

    const panels = Array.from(sidebar.querySelectorAll(MOBILE_PANEL));
    if (!panels.length) return;

    mobileSidebarGuardInstalled = true;

    panels.forEach((panel) => {
      const contentHost = panel.querySelector(MOBILE_PANEL_CONTENT);
      const footerHost = panel.querySelector(MOBILE_PANEL_FOOTER);

      if (!contentHost && !footerHost) return;

      const contentFragment = parkChildren(contentHost);
      const footerFragment = parkChildren(footerHost);

      mobilePanelPayloads.set(panel, {
        contentHost,
        contentFragment,
        footerHost,
        footerFragment,
        hydrated: false
      });

      panel.dataset.llclPayloadParked = '1';
    });

    const hydrateAll = () => {
      panels.forEach(hydrateMobilePanel);
    };

    const resolveControlledPanel = (trigger) => {
      const id = trigger?.getAttribute?.('aria-controls');
      if (!id) return null;

      const panel = document.getElementById(id);
      if (!panel || !panel.matches(MOBILE_PANEL) || !sidebar.contains(panel)) return null;
      return panel;
    };

    const onMenuIntent = (event) => {
      if (
        event.type === 'keydown' &&
        event.key !== 'Enter' &&
        event.key !== ' ' &&
        event.key !== 'Spacebar'
      ) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const drawerTrigger = target.closest(MOBILE_DRAWER_TRIGGER);
      if (drawerTrigger && !sidebar.contains(drawerTrigger)) {
        hydrateAll();
        return;
      }

      const panelTrigger = target.closest(MOBILE_PANEL_TRIGGER);
      if (!panelTrigger || !sidebar.contains(panelTrigger)) return;

      hydrateMobilePanel(resolveControlledPanel(panelTrigger));
    };

    document.addEventListener('pointerdown', onMenuIntent, true);
    document.addEventListener('keydown', onMenuIntent, true);
    document.addEventListener('click', onMenuIntent, true);

    window.__LLCL_MOBILE_SIDEBAR_DOM_GUARD__ = {
      hydrateAll,
      hydratePanel: hydrateMobilePanel
    };
  };

  const hydrateMobileHiddenMegaBlocks = () => {
    if (!mobileMegaBlockPayloads.length) return;

    mobileMegaBlockPayloads.forEach(({ block, marker }) => {
      if (marker?.isConnected) marker.replaceWith(block);
    });

    mobileMegaBlockPayloads.length = 0;

    const layer = document.querySelector(MOBILE_HIDDEN_MEGA_LAYER);
    if (layer) {
      layer.dataset.llclPayloadParked = '0';
      layer.dataset.llclPayloadHydrated = '1';
    }
  };

  const installMobileHiddenMegaDomGuard = () => {
    if (
      mobileMegaLayerGuardInstalled ||
      window.Shopify?.designMode ||
      desktop.matches
    ) {
      return;
    }

    const blocks = Array.from(document.querySelectorAll(MOBILE_HIDDEN_MEGA_BLOCK));
    if (!blocks.length) return;

    mobileMegaLayerGuardInstalled = true;

    blocks.forEach((block) => {
      const marker = document.createComment('llcl-mega-block');
      block.before(marker);
      block.remove();
      mobileMegaBlockPayloads.push({ block, marker });
    });

    const layer = document.querySelector(MOBILE_HIDDEN_MEGA_LAYER);
    if (layer) layer.dataset.llclPayloadParked = '1';

    const onViewportChange = (event) => {
      if (!event.matches) return;
      hydrateMobileHiddenMegaBlocks();
      desktop.removeEventListener?.('change', onViewportChange);
    };

    desktop.addEventListener?.('change', onViewportChange);

    window.__LLCL_MOBILE_MEGA_DOM_GUARD__ = {
      hydrate: hydrateMobileHiddenMegaBlocks
    };
  };

  const cleanup = () => {
    document.removeEventListener('pointerover', onHeaderIntent, true);
    document.removeEventListener('pointerdown', onHeaderIntent, true);
    document.removeEventListener('focusin', onHeaderIntent, true);
  };

  const start = async () => {
    if (started || !desktop.matches) return;
    started = true;
    cleanup();
    installMegaImageGuard();
    await Promise.all(cssUrls.map(loadCss));
    for (const src of jsUrls) await loadScript(src);
  };

  function onHeaderIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(HEADER)) start();
  }

  installHomeA11ySemantics();
  installVideoPayloadGuard();
  installDeferredSectionScripts();
  installDeferredInlineInitializers();
  installMobileSidebarDomGuard();
  installMobileHiddenMegaDomGuard();

  if (!desktop.matches) return;

  document.addEventListener('pointerover', onHeaderIntent, true);
  document.addEventListener('pointerdown', onHeaderIntent, true);
  document.addEventListener('focusin', onHeaderIntent, true);
})();
